import { Material, OneArray, TwoArray } from 'Types';

import {
  numberFormatter,
  randomInt,
  sample,
  sumBy,
  without,
} from 'utils/utils';

type Opaque<T, K> = T & { _: K };

// Pattern String
export type PatternString = Opaque<string, 'PatternString'>;
export type MaterialPatternString = PatternString &
  Opaque<PatternString, 'MaterialPatternString'>;
export type SubBoardPatternString = PatternString &
  Opaque<PatternString, 'SubBoardPatternString'>;
export type SizeOnlyPatternString = PatternString &
  Opaque<PatternString, 'SizeOnlyPatternString'>;

// Pattern String Fragment
export type PatternStringFragment = Opaque<string, 'PatternStringFragment'>;
export type MaterialPatternStringFragment = PatternStringFragment &
  Opaque<PatternStringFragment, 'MaterialPatternStringFragment'>;
export type SubBoardPatternStringFragment = PatternStringFragment &
  Opaque<PatternStringFragment, 'SubBoardPatternStringFragment'>;
export type SizeOnlyPatternStringFragment = PatternStringFragment &
  Opaque<PatternStringFragment, 'SizeOnlyPatternStringFragment'>;

// Pattern Fragments
export type MaterialPatternFragment = {
  key: Material | null;
  size: number;
};
export type SubBoardPatternFragment = {
  key: string | null;
  size: number;
  flip: boolean;
};
export type SizeOnlyPatternFragment = {
  size: number;
};
export type PatternFragment =
  | MaterialPatternFragment
  | SubBoardPatternFragment
  | SizeOnlyPatternFragment;

// Patterns
export type Pattern = Array<PatternFragment>;
export type MaterialPattern = Array<MaterialPatternFragment>;
export type SubBoardPattern = Array<SubBoardPatternFragment>;
export type SizeOnlyPattern = Array<SizeOnlyPatternFragment>;

// Mapping types

export type PatternByPatternString<T extends PatternString> =
  T extends MaterialPatternString
    ? MaterialPattern
    : T extends SubBoardPatternString
    ? SubBoardPattern
    : T extends SizeOnlyPatternString
    ? SubBoardPattern
    : never;

export type PatternStringByPattern<T extends Pattern> =
  T extends MaterialPattern
    ? MaterialPatternString
    : T extends SubBoardPattern
    ? SubBoardPatternString
    : T extends SizeOnlyPattern
    ? SizeOnlyPatternString
    : never;

export function makeMaterialPatternFragment(
  size: number,
  material: Material | null
): MaterialPatternFragment {
  return { size, key: material };
}

export function makeSubBoardPatternFragment(
  size: number,
  key: string | null,
  flip: boolean
): SubBoardPatternFragment {
  return { size, key: key, flip: flip };
}

export function makeSizeOnlyPatternFragment(
  size: number
): SubBoardPatternFragment {
  return { size, key: null, flip: false };
}

export function toPatternString<
  T extends Pattern,
  R extends PatternStringByPattern<T>
>(pattern: Pattern): R {
  return pattern
    .map(
      (fragment) =>
        ('size' in fragment ? fragment.size : '') +
        ('key' in fragment ? fragment.key ?? '' : '') +
        ('flip' in fragment && fragment.flip ? '^' : '')
    )
    .join(' ') as R;
}

export function parseMaterialPatternString(
  patternString: MaterialPatternString
): MaterialPattern {
  return splitPatternString(patternString).map((fragmentString) => {
    const materialMatch = fragmentString
      .match(/[a-z|A-Z]+/)?.[0]
      ?.toUpperCase();
    const material = Material[materialMatch as Material] ?? null;

    return {
      key: material,
      size: parseFloat(fragmentString) || 0,
      flip: false,
    };
  });
}

export function parseSubBoardPatternString(
  patternString: SubBoardPatternString
): SubBoardPattern {
  return splitPatternString(patternString).map((fragmentString) => {
    const keyMatch = fragmentString.match(/[a-z|A-Z]+/)?.[0]?.toUpperCase();

    return {
      key: keyMatch ?? null,
      size: parseFloat(fragmentString) || 0,
      flip: fragmentString[fragmentString.length - 1] === '^',
    };
  });
}
export function parseSizeOnlyPatternString(
  patternString: SubBoardPatternString
): SubBoardPattern {
  return splitPatternString(patternString).map((fragmentString) => {
    return {
      key: null,
      size: parseFloat(fragmentString) || 0,
      flip: false,
    };
  });
}

export function parsePatternString<
  T extends PatternString,
  R extends PatternByPatternString<T>
>(patternString: T): R {
  return splitPatternString(patternString).map((fragmentString) => {
    const keyMatch = fragmentString.match(/[a-z|A-Z]+/)?.[0]?.toUpperCase();

    return {
      key: keyMatch ?? null,
      size: parseFloat(fragmentString) || 0,
      flip: fragmentString[fragmentString.length - 1] === '^',
    };
  }) as R;
}

export function splitPatternString<T extends PatternString>(
  patternString: T
): Array<string> {
  return patternString.trim().split(/\s+/).filter(Boolean);
}

export function getPatternStringSize(patternString: PatternString): number {
  return splitPatternString(patternString).length;
}

export function spacePattern<T extends Pattern>(
  pattern: T,
  spacer: number = 0.25
): T {
  return pattern
    .map((pattern) => [pattern, { size: spacer, key: null, flip: false }])
    .flat() as T;
}

export function reversePatternString<T extends PatternString>(
  patternString: T
): T {
  return splitPatternString(patternString).reverse().join(' ') as T;
}

export function sumPattern(pattern: Array<{ size: number }>): number {
  return sumBy(pattern, ({ size }) => size);
}

export function changePatternSize<
  T extends MaterialPatternString | SubBoardPatternString
>(pattern: T, targetIndex: number, size: number): T {
  return toPatternString(
    parsePatternString(pattern).map((fragment, index) => ({
      ...fragment,
      size: index === targetIndex ? size : fragment.size,
    }))
  );
}

export function changePatternMaterial(
  pattern: MaterialPatternString,
  targetIndex: number,
  material: Material
): MaterialPatternString {
  return toPatternString(
    parseMaterialPatternString(pattern).map((fragment, index) => ({
      ...fragment,
      key: index === targetIndex ? material : fragment.key,
    }))
  );
}

export function scalePatternString<
  T extends MaterialPatternString | SubBoardPatternString
>(patternString: T, scaleFactor: number): T {
  return toPatternString(
    parsePatternString(patternString).map((fragment) => ({
      ...fragment,
      size: +numberFormatter.format(fragment.size * scaleFactor),
    }))
  );
}

// T will usually be Material, but it might be [A, B, ...] for Patterns
export function createRandomMaterialPattern(
  min: number,
  max: number,
  totalSize: number,
  fragmentTypes: TwoArray<Material>,
  previousFragmentType?: Material
): MaterialPattern {
  const pattern: MaterialPattern = [];
  let sizeAcc = 0;
  let prevFragmentType: Material | null = previousFragmentType ?? null;

  while (sizeAcc < totalSize) {
    // Filter out previous fragmentType
    const acceptableFragmentTypes = without(
      fragmentTypes,
      // eslint-disable-next-line no-loop-func
      (f) => f !== prevFragmentType
    );

    const randomFragment = createRandomPatternFragment(
      min,
      max,
      acceptableFragmentTypes
    );

    pattern.push({
      key: randomFragment.key,
      // If this random fragment makes this pattern too long, cut it short
      size:
        randomFragment.size + sizeAcc <= totalSize
          ? randomFragment.size
          : totalSize - sizeAcc,
    });
    sizeAcc += randomFragment.size;
    prevFragmentType = randomFragment.key;
  }

  return pattern;
}

// T will usually be Material, but it might be [A, B, ...] for Patterns
export function createRandomMaterialPatternString(
  min: number,
  max: number,
  totalSize: number,
  fragmentTypes: TwoArray<Material>,
  previousFragmentType?: Material
): MaterialPatternString {
  return toPatternString(
    createRandomMaterialPattern(
      min,
      max,
      totalSize,
      fragmentTypes,
      previousFragmentType
    )
  );
}

// T will usually be Material, but it might be [A, B, ...] for Patterns
export function createRandomPatternFragment<T>(
  min: number,
  max: number,
  fragmentTypes: OneArray<T>
): {
  size: number;
  key: T;
  flip: boolean;
} {
  return {
    size: randomInt(min * 8, max * 8) / 8,
    key: sample(fragmentTypes),
    flip: !!randomInt(0, 1),
  };
}

// T will usually be Material, but it might be [A, B, ...] for Patterns
export function createRandomSubBoardPatternString(
  min: number,
  max: number,
  totalSize: number,
  fragmentTypes: TwoArray<string>
): SubBoardPatternString {
  let pattern = [];
  let sizeAcc = 0;
  let fragmentType = '';

  while (sizeAcc < totalSize) {
    // Filter out previous fragmentType
    const acceptableFragmentTypes =
      fragmentType === ''
        ? fragmentTypes
        : without(fragmentTypes, (f) => f !== fragmentType);

    const randomFragment = createRandomPatternFragment(
      min,
      max,
      acceptableFragmentTypes
    );

    pattern.push({
      key: randomFragment.key,
      // If this random fragment makes this pattern too long, cut it short
      size:
        randomFragment.size + sizeAcc <= totalSize
          ? randomFragment.size
          : totalSize - sizeAcc,
      flip: randomFragment.flip,
    });
    sizeAcc += randomFragment.size;
  }

  return toPatternString(pattern);
}
